/**
 * AI Bill Scan — upload a supplier purchase invoice (PDF/image), let the AI
 * extract fields, resolve any ambiguities with the user, then stage a
 * purchase.create pending action through the Phase 2A confirmation loop.
 *
 * The AI never writes to inventory. This page simply prepares a proposal and
 * hands it to the existing pending-action flow.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, ScanLine, AlertTriangle, Loader2, CheckCircle2, RefreshCw, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/Bits";
import { Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, today } from "@/lib/format";
import BarcodeScanner, { ScanButton } from "@/components/BarcodeScanner";

const isoDate = (s) => (s || "").slice(0, 10) || "";

export default function BillScan() {
  const { can } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [invoiceMeta, setInvoiceMeta] = useState({ invoice_no: "", invoice_date: today(),
    purchase_date: today(), interstate: false, notes: "" });
  const [items, setItems] = useState([]);
  const [staging, setStaging] = useState(false);
  const [staged, setStaged] = useState(null);
  const [scanRow, setScanRow] = useState(null);

  useEffect(() => {
    api.get("/pharmacy/suppliers", { params: { status: "active" } })
      .then(({ data }) => setSuppliers(data.items || []))
      .catch(() => setSuppliers([]));
  }, []);

  if (!can("pharmacy.purchase.create")) return <NoAccess what="the AI bill scan" />;

  const upload = async () => {
    if (!file) { toast.error("Choose a PDF or image of the supplier invoice."); return; }
    setBusy(true); setProposal(null); setStaged(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/bill-scan", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setProposal(data.proposal);
      const sup = data.proposal.supplier?.matched;
      if (sup) setSupplierId(sup.supplier_id);
      setInvoiceMeta((m) => ({
        ...m,
        invoice_no: (data.proposal.invoice || {}).invoice_no || "",
        invoice_date: isoDate((data.proposal.invoice || {}).invoice_date) || m.invoice_date,
        notes: (data.proposal.invoice || {}).notes || "",
      }));
      setItems((data.proposal.items || []).map((it) => ({
        medicine_id: it.medicine_id || "",
        medicine_name: it.medicine_name || it.extracted?.name || "",
        candidates: it.candidates || [],
        issues: it.issues || [],
        batch_no: it.extracted?.batch_no || "",
        expiry_date: isoDate(it.extracted?.expiry_date),
        quantity: it.extracted?.quantity ?? "",
        free_quantity: it.extracted?.free_quantity ?? 0,
        purchase_price: it.extracted?.purchase_price ?? "",
        mrp: it.extracted?.mrp ?? "",
        selling_price: it.extracted?.selling_price ?? it.extracted?.mrp ?? "",
        gst_percent: it.extracted?.gst_percent ?? 12,
        discount_percent: it.extracted?.discount_percent ?? 0,
      })));
      toast.success("Extraction complete. Please review before staging.");
    } catch (e) {
      toast.error(apiError(e) || "AI extraction failed.");
    } finally { setBusy(false); }
  };

  const patchItem = (idx, patch) =>
    setItems((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeItem = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));

  const useBarcodeForRow = async (code) => {
    if (scanRow === null) return;
    try {
      const { data } = await api.get("/pharmacy/medicines/by-barcode", { params: { code } });
      if (!data.medicine) {
        toast.error(`No medicine with barcode ${code} is available in this business.`);
        return;
      }
      const medicine = data.medicine;
      patchItem(scanRow, {
        medicine_id: medicine.medicine_id,
        medicine_name: medicine.name,
        candidates: [{ medicine_id: medicine.medicine_id, name: medicine.name, code: medicine.code }],
      });
      toast.success(`${medicine.name} selected for this scanned invoice row.`);
      setScanRow(null);
    } catch (requestError) {
      toast.error(apiError(requestError) || "Barcode lookup failed.");
    }
  };

  const stage = async () => {
    if (!supplierId) { toast.error("Select the supplier before staging."); return; }
    if (!items.length) { toast.error("Add at least one item."); return; }
    setStaging(true);
    try {
      const payload = {
        supplier_id: supplierId,
        invoice_no: invoiceMeta.invoice_no,
        invoice_date: invoiceMeta.invoice_date,
        purchase_date: invoiceMeta.purchase_date,
        interstate: !!invoiceMeta.interstate,
        notes: invoiceMeta.notes,
        items: items.map((it) => ({
          medicine_id: it.medicine_id, medicine_name: it.medicine_name,
          batch_no: it.batch_no, expiry_date: it.expiry_date,
          quantity: Number(it.quantity || 0),
          free_quantity: Number(it.free_quantity || 0),
          purchase_price: Number(it.purchase_price || 0),
          mrp: Number(it.mrp || 0), selling_price: Number(it.selling_price || it.mrp || 0),
          gst_percent: Number(it.gst_percent || 0),
          discount_percent: Number(it.discount_percent || 0),
        })),
      };
      const { data } = await api.post("/ai/bill-scan/stage", payload);
      if (data.status === "need_fields") {
        toast.error("Fill in the missing fields on the rows highlighted below.");
        setStaged({ needFields: data.missing });
        return;
      }
      setStaged({ pending: data.pending_action, duplicate: data.duplicate_of });
      toast.success("Purchase staged — open Purchases to confirm.");
    } catch (e) {
      toast.error(apiError(e) || "Could not stage this purchase.");
    } finally { setStaging(false); }
  };

  const confirmStaged = async () => {
    if (!staged?.pending?.action_id) return;
    try {
      const { data } = await api.post(`/ai/pending-actions/${staged.pending.action_id}`,
        { decision: "confirm" });
      if (data.ok) toast.success(data.message || "Purchase recorded.");
      else toast.error(data.message || "Could not record the purchase.");
      setStaged(null); setProposal(null); setItems([]); setFile(null);
    } catch (e) { toast.error(apiError(e) || "Confirmation failed."); }
  };

  return (
    <div data-testid="ai-bill-scan-page">
      <PageHeader overline="Pharmacy · AI" title="AI Bill Scan"
        subtitle="Upload a supplier invoice — the AI reads it, you review, we stage a purchase for confirmation. Nothing is committed until you approve." />

      <div className="panel p-5 mb-6">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Purchase invoice (PDF, PNG, JPG or WEBP · up to 15 MB)">
            <input type="file" accept=".pdf,image/*" data-testid="bill-scan-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700" />
          </Field>
          <button onClick={upload} disabled={busy || !file}
            className={primaryBtnCls} data-testid="bill-scan-extract-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
            Extract with AI
          </button>
        </div>
      </div>

      {busy && <Loading label="Reading your invoice with AI…" />}

      {proposal && !busy && (
        <div className="space-y-6" data-testid="bill-scan-review">
          {(proposal.uncertainties || []).length > 0 && (
            <div className="panel p-4 border-amber-200 bg-amber-50" data-testid="bill-scan-uncertainties">
              <div className="flex items-start gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <div>
                  <p className="font-semibold">Things the AI is not sure about — please review:</p>
                  <ul className="mt-2 list-disc pl-4 space-y-1 text-sm">
                    {proposal.uncertainties.map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {proposal.duplicate_of && (
            <div className="panel p-4 border-red-200 bg-red-50 text-red-800" data-testid="bill-scan-duplicate">
              <p className="font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Duplicate supplier invoice detected
              </p>
              <p className="text-sm mt-1">
                Invoice <strong>{proposal.invoice?.invoice_no}</strong> from this supplier was already recorded as <strong>{proposal.duplicate_of.purchase_no}</strong> on {proposal.duplicate_of.purchase_date}. You will not be allowed to stage another unless an admin overrides it during confirmation.
              </p>
            </div>
          )}

          <div className="panel p-5">
            <p className="overline text-[#EA580C] mb-4">Supplier &amp; invoice</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Supplier *">
                <select className={inputCls} value={supplierId} data-testid="bill-scan-supplier"
                  onChange={(e) => setSupplierId(e.target.value)}>
                  <option value="">Select supplier</option>
                  {suppliers.map((s) => (
                    <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                  ))}
                </select>
                {proposal.supplier?.extracted?.name && (
                  <p className="mt-1 text-xs text-slate-500">
                    Extracted: {proposal.supplier.extracted.name}
                    {proposal.supplier.extracted.gstin && ` · ${proposal.supplier.extracted.gstin}`}
                  </p>
                )}
              </Field>
              <Field label="Invoice no.">
                <input className={inputCls} value={invoiceMeta.invoice_no} data-testid="bill-scan-invoice-no"
                  onChange={(e) => setInvoiceMeta({ ...invoiceMeta, invoice_no: e.target.value })} />
              </Field>
              <Field label="Invoice date">
                <input type="date" className={inputCls} value={invoiceMeta.invoice_date}
                  onChange={(e) => setInvoiceMeta({ ...invoiceMeta, invoice_date: e.target.value })} />
              </Field>
              <Field label="Purchase date">
                <input type="date" className={inputCls} value={invoiceMeta.purchase_date}
                  onChange={(e) => setInvoiceMeta({ ...invoiceMeta, purchase_date: e.target.value })} />
              </Field>
              <Field label="Interstate purchase (IGST)">
                <select className={inputCls} value={String(invoiceMeta.interstate)}
                  onChange={(e) => setInvoiceMeta({ ...invoiceMeta, interstate: e.target.value === "true" })}>
                  <option value="false">No — CGST + SGST</option>
                  <option value="true">Yes — IGST</option>
                </select>
              </Field>
              <Field label="Notes"><input className={inputCls} value={invoiceMeta.notes}
                onChange={(e) => setInvoiceMeta({ ...invoiceMeta, notes: e.target.value })} /></Field>
            </div>
          </div>

          <div className="panel overflow-x-auto">
            {items.length === 0 ? (
              <EmptyState title="No line items extracted" subtitle="Upload a clearer scan and try again." />
            ) : (
              <table className="w-full text-sm" data-testid="bill-scan-items-table">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-3">Medicine *</th>
                    <th className="px-3 py-3">Batch *</th>
                    <th className="px-3 py-3">Expiry *</th>
                    <th className="px-3 py-3 text-right">Qty *</th>
                    <th className="px-3 py-3 text-right">Free</th>
                    <th className="px-3 py-3 text-right">Rate *</th>
                    <th className="px-3 py-3 text-right">MRP</th>
                    <th className="px-3 py-3 text-right">GST %</th>
                    <th className="px-3 py-3 text-right">Disc %</th>
                    <th className="px-3 py-3">Issues</th>
                    <th className="px-3 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it, i) => (
                    <tr key={i} data-testid={`bill-scan-row-${i}`}>
                      <td className="px-3 py-2 min-w-[200px]">
                        <div className="flex gap-2">
                          <select className={inputCls} value={it.medicine_id}
                            onChange={(e) => patchItem(i, { medicine_id: e.target.value,
                              medicine_name: it.candidates.find((c) => c.medicine_id === e.target.value)?.name || it.medicine_name })}
                            data-testid={`bill-scan-medicine-${i}`}>
                            <option value="">Select — {it.medicine_name}</option>
                            {(it.candidates || []).map((c) => (
                              <option key={c.medicine_id} value={c.medicine_id}>{c.name} ({c.code})</option>
                            ))}
                          </select>
                          <ScanButton label="Scan" onClick={() => setScanRow(i)}
                            testid={`bill-scan-barcode-${i}`} />
                        </div>
                      </td>
                      <td className="px-3 py-2"><input className={inputCls} value={it.batch_no}
                        onChange={(e) => patchItem(i, { batch_no: e.target.value })}
                        data-testid={`bill-scan-batch-${i}`} /></td>
                      <td className="px-3 py-2"><input type="date" className={inputCls} value={it.expiry_date}
                        onChange={(e) => patchItem(i, { expiry_date: e.target.value })}
                        data-testid={`bill-scan-expiry-${i}`} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-20 text-right`} value={it.quantity}
                        onChange={(e) => patchItem(i, { quantity: e.target.value })}
                        data-testid={`bill-scan-qty-${i}`} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-16 text-right`} value={it.free_quantity}
                        onChange={(e) => patchItem(i, { free_quantity: e.target.value })} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-24 text-right`} value={it.purchase_price}
                        onChange={(e) => patchItem(i, { purchase_price: e.target.value })}
                        data-testid={`bill-scan-rate-${i}`} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-24 text-right`} value={it.mrp}
                        onChange={(e) => patchItem(i, { mrp: e.target.value })} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-16 text-right`} value={it.gst_percent}
                        onChange={(e) => patchItem(i, { gst_percent: e.target.value })} /></td>
                      <td className="px-3 py-2"><input type="number" className={`${inputCls} !w-16 text-right`} value={it.discount_percent}
                        onChange={(e) => patchItem(i, { discount_percent: e.target.value })} /></td>
                      <td className="px-3 py-2 text-xs text-amber-700">
                        {(it.issues || []).map((iss, j) => <div key={j}>{iss}</div>)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeItem(i)} className="text-red-600" aria-label="Remove row"
                          data-testid={`bill-scan-remove-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button className={ghostBtnCls} onClick={() => { setProposal(null); setItems([]); setStaged(null); setFile(null); }}
              data-testid="bill-scan-reset-btn">
              <RefreshCw className="h-4 w-4" /> Start over
            </button>
            <button onClick={stage} disabled={staging || !items.length}
              className={primaryBtnCls} data-testid="bill-scan-stage-btn">
              {staging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Stage for confirmation
            </button>
          </div>

          {staged?.needFields && (
            <div className="panel p-4 border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="bill-scan-missing">
              <p className="font-semibold mb-2">Missing information — fix these rows before staging:</p>
              <ul className="list-disc pl-4 space-y-1">
                {staged.needFields.map((m, i) => (
                  <li key={i}>Row {m.row} ({m.name}): {(m.missing || []).join(", ")}</li>
                ))}
              </ul>
            </div>
          )}

          {staged?.pending && (
            <div className="panel p-5 border-emerald-200 bg-emerald-50 text-emerald-900"
              data-testid="bill-scan-staged">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-semibold">{staged.pending.summary}</p>
                  <p className="mt-1 text-sm">
                    {staged.pending.preview?.item_count} item(s) · Est. total{" "}
                    <strong>{inr(staged.pending.preview?.estimated_total || 0)}</strong>
                  </p>
                  {staged.duplicate && (
                    <p className="mt-2 text-sm text-red-700">
                      ⚠ Existing purchase {staged.duplicate.purchase_no} matches this invoice. Confirmation will require admin override.
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button onClick={confirmStaged} className={primaryBtnCls}
                      data-testid="bill-scan-confirm-btn">Confirm &amp; record purchase</button>
                    <button onClick={() => setStaged(null)} className={ghostBtnCls}>Keep editing</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {scanRow !== null && (
        <BarcodeScanner
          testid="bill-scan-barcode-scanner"
          onScan={useBarcodeForRow}
          onClose={() => setScanRow(null)}
        />
      )}
    </div>
  );
}
